import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import ClientsManager from './ClientsManager'
import * as FiIcons from 'react-icons/fi'

const crmTabs = [
  ['overview', 'Overview', FiIcons.FiHome],
  ['services', 'Services', FiIcons.FiLayers],
  ['locations', 'Locations', FiIcons.FiMapPin],
  ['partners', 'Partners', FiIcons.FiUserCheck],
  ['equipment', 'Equipment', FiIcons.FiCpu],
  ['employees', 'Employees', FiIcons.FiUsers],
  ['credentials', 'Credentials', FiIcons.FiKey],
  ['jobs', 'Jobs', FiIcons.FiTool]
]

function money(value) {
  if (value === null || value === undefined || value === '') return '—'
  return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0)
}

function dateTime(value) {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('el-GR', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value))
  } catch {
    return value
  }
}

function statusClass(status) {
  const value = String(status || '').toLowerCase()
  if (['active', 'connected', 'completed', 'done', 'paid'].includes(value)) return 'bg-green-100 text-green-800'
  if (['issue', 'failed', 'overdue', 'cancelled', 'inactive'].includes(value)) return 'bg-red-100 text-red-800'
  if (['waiting', 'scheduled', 'pending', 'in_progress', 'postponed'].includes(value)) return 'bg-yellow-100 text-yellow-800'
  return 'bg-slate-100 text-slate-700'
}

function Badge({ children, status }) {
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusClass(status || children)}`}>{children || '—'}</span>
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-50 p-2 text-blue-600"><SafeIcon icon={icon} className="h-5 w-5" /></div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
          <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 break-words text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  )
}

function Section({ title, subtitle, children, action }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function CredentialValue({ value }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch (err) {
      console.error(err)
    }
  }
  return (
    <div className="flex items-center gap-2">
      <span className="min-w-0 break-all font-mono text-xs text-slate-800">{value || '—'}</span>
      {value ? (
        <button onClick={copy} className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200">
          {copied ? 'Copied' : 'Copy'}
        </button>
      ) : null}
    </div>
  )
}

export default function ClientCRMHub() {
  const [mode, setMode] = useState('crm')
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [serviceFilter, setServiceFilter] = useState('all')
  const [selected, setSelected] = useState(null)
  const [details, setDetails] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    if (mode === 'crm') fetchClients()
  }, [mode])

  async function fetchClients() {
    try {
      setLoading(true)
      setError('')
      const { data, error: rpcError } = await supabase.rpc('codevaro_clients_360_list')
      if (rpcError) throw rpcError
      setClients(data || [])
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία φόρτωσης CRM: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  async function openClient(client) {
    try {
      setSelected(client)
      setDetails(null)
      setActiveTab('overview')
      setDetailsLoading(true)
      setError('')
      const { data, error: rpcError } = await supabase.rpc('codevaro_client_360', { p_client_id: client.id })
      if (rpcError) throw rpcError
      setDetails(data || {})
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία φόρτωσης πελάτη: ${err.message}`)
    } finally {
      setDetailsLoading(false)
    }
  }

  const availableServices = useMemo(() => {
    const set = new Set()
    clients.forEach((client) => (client.services || []).forEach((service) => set.add(service)))
    return Array.from(set).sort()
  }, [clients])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return clients.filter((client) => {
      const services = client.services || []
      const matchesService = serviceFilter === 'all' || services.includes(serviceFilter)
      const haystack = [client.name, client.legal_name, client.email, client.phone, client.vat_number, client.primary_contact_name, ...services]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return matchesService && (!term || haystack.includes(term))
    })
  }, [clients, search, serviceFilter])

  if (mode === 'management') {
    return (
      <div className="space-y-4">
        <ViewSwitcher mode={mode} setMode={setMode} />
        <ClientsManager />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ViewSwitcher mode={mode} setMode={setMode} />

      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Clients CRM 360°</h1>
          <p className="mt-1 text-sm text-slate-600">Πελάτες, υπηρεσίες, συνεργάτες, εγκαταστάσεις, εξοπλισμός, εργαζόμενοι, credentials και εργασίες σε μία εικόνα.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Αναζήτηση πελάτη, ΑΦΜ, τηλέφωνο..."
            className="min-w-[280px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="all">Όλες οι υπηρεσίες</option>
            {availableServices.map((service) => <option key={service} value={service}>{service}</option>)}
          </select>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

      {loading ? (
        <div className="flex h-52 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filtered.map((client) => (
            <button key={client.id} onClick={() => openClient(client)} className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{client.name}</h2>
                  <div className="mt-1 text-sm text-slate-500">{client.legal_name || client.primary_contact_name || '—'}</div>
                </div>
                <Badge status={client.status}>{client.status || 'active'}</Badge>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {(client.services || []).length ? (client.services || []).map((service) => (
                  <span key={service} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">{service}</span>
                )) : <span className="text-xs text-slate-400">Χωρίς υπηρεσία</span>}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-center sm:grid-cols-6">
                <MiniStat label="Locations" value={client.locations_count} />
                <MiniStat label="Assets" value={client.assets_count} />
                <MiniStat label="Employees" value={client.employees_count} />
                <MiniStat label="Partners" value={client.partners_count} />
                <MiniStat label="Jobs" value={client.jobs_count} />
                <MiniStat label="Open" value={client.open_jobs_count} />
              </div>

              <div className="mt-4 flex flex-col gap-1 border-t border-slate-100 pt-4 text-xs text-slate-500 sm:flex-row sm:justify-between">
                <span>Recurring: <strong className="text-slate-700">{money(client.recurring_revenue)}</strong></span>
                <span>Next: <strong className="text-slate-700">{dateTime(client.next_appointment)}</strong></span>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-3 backdrop-blur-sm sm:p-6">
          <div className="mx-auto min-h-[90vh] max-w-7xl overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-bold text-slate-900">{selected.name}</h2>
                    <Badge status={selected.status}>{selected.status}</Badge>
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{selected.phone || '—'} · {selected.email || '—'} · ΑΦΜ {selected.vat_number || '—'}</div>
                </div>
                <button onClick={() => { setSelected(null); setDetails(null) }} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                  <SafeIcon icon={FiIcons.FiX} className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                {crmTabs.map(([key, label, icon]) => (
                  <button key={key} onClick={() => setActiveTab(key)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm ${activeTab === key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    <SafeIcon icon={icon} className="h-4 w-4" />{label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5 sm:p-6">
              {detailsLoading || !details ? <div className="flex h-64 items-center justify-center"><div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" /></div> : <ClientDetailTab tab={activeTab} client={selected} details={details} />}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ViewSwitcher({ mode, setMode }) {
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
      <button onClick={() => setMode('crm')} className={`rounded-lg px-4 py-2 text-sm font-medium ${mode === 'crm' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>CRM 360°</button>
      <button onClick={() => setMode('management')} className={`rounded-lg px-4 py-2 text-sm font-medium ${mode === 'management' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>Basic / Portal Management</button>
    </div>
  )
}

function MiniStat({ label, value }) {
  return <div><div className="text-lg font-semibold text-slate-900">{value || 0}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div></div>
}

function ClientDetailTab({ tab, client, details }) {
  if (tab === 'overview') return <Overview client={client} details={details} />
  if (tab === 'services') return <Services services={details.services || []} />
  if (tab === 'locations') return <Locations locations={details.locations || []} />
  if (tab === 'partners') return <Partners partners={details.partners || []} />
  if (tab === 'equipment') return <Equipment assets={details.assets || []} />
  if (tab === 'employees') return <Employees employees={details.employees || []} />
  if (tab === 'credentials') return <Credentials credentials={details.credentials || []} />
  if (tab === 'jobs') return <Jobs jobs={details.jobs || []} />
  return null
}

function Overview({ client, details }) {
  const services = details.services || []
  const jobs = details.jobs || []
  const recurring = services.reduce((sum, item) => sum + Number(item.recurring_fee || 0), 0)
  const openJobs = jobs.filter((job) => !['completed', 'done', 'cancelled'].includes(String(job.status || '').toLowerCase())).length
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Services" value={services.length} icon={FiIcons.FiLayers} />
        <StatCard label="Locations" value={(details.locations || []).length} icon={FiIcons.FiMapPin} />
        <StatCard label="Assets" value={(details.assets || []).length} icon={FiIcons.FiCpu} />
        <StatCard label="Employees" value={(details.employees || []).length} icon={FiIcons.FiUsers} />
        <StatCard label="Open Jobs" value={openJobs} icon={FiIcons.FiTool} />
        <StatCard label="Recurring" value={money(recurring)} icon={FiIcons.FiRepeat} />
      </div>
      <Section title="Client profile">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <Field label="Client / person" value={client.name} />
          <Field label="Legal name" value={client.legal_name} />
          <Field label="VAT" value={client.vat_number} mono />
          <Field label="Phone" value={client.phone} />
          <Field label="Email" value={client.email} />
          <Field label="Primary contact" value={client.primary_contact_name} />
        </div>
      </Section>
      <Section title="Active services">
        <div className="flex flex-wrap gap-2">{services.map((service) => <span key={service.id} className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700">{service.service_title}{service.location_name ? ` · ${service.location_name}` : ''}</span>)}</div>
      </Section>
    </div>
  )
}

function Services({ services }) {
  return <div className="space-y-4">{services.length ? services.map((service) => (
    <Section key={service.id} title={service.service_title} subtitle={service.location_name || service.display_name || 'Client level service'} action={<Badge status={service.status}>{service.status}</Badge>}>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Field label="Billing" value={service.billing_type} />
        <Field label="Setup fee" value={money(service.setup_fee)} />
        <Field label="Recurring" value={money(service.recurring_fee)} />
        <Field label="Cycle" value={service.billing_cycle} />
        <Field label="Next billing" value={service.next_billing_date} />
      </div>
      {service.values?.length ? <div className="mt-5 grid grid-cols-1 gap-4 rounded-xl bg-slate-50 p-4 md:grid-cols-3">{service.values.map((field, index) => <Field key={`${field.field_key}-${index}`} label={field.label || field.field_key} value={field.value_text || (field.value_json ? JSON.stringify(field.value_json) : '')} mono={field.field_type === 'password' || field.is_secret} />)}</div> : null}
      {service.notes ? <div className="mt-4 text-sm text-slate-600">{service.notes}</div> : null}
      <div className="mt-4 text-xs text-slate-400">Source: {service.source || 'manual'} {service.source_ref ? `· ${service.source_ref}` : ''}</div>
    </Section>
  )) : <Empty label="Δεν υπάρχουν υπηρεσίες." />}</div>
}

function Locations({ locations }) {
  return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{locations.length ? locations.map((location) => (
    <Section key={location.id} title={location.name} action={location.is_primary ? <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">Primary</span> : null}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Type" value={location.location_type} />
        <Field label="Branch code" value={location.branch_code} />
        <Field label="Address" value={location.address} />
        <Field label="City" value={location.city} />
        <Field label="Phone" value={location.phone} />
        <Field label="Email" value={location.email} />
      </div>
      {location.notes ? <p className="mt-4 text-sm text-slate-600">{location.notes}</p> : null}
    </Section>
  )) : <Empty label="Δεν υπάρχουν εγκαταστάσεις." />}</div>
}

function Partners({ partners }) {
  return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{partners.length ? partners.map((partner) => (
    <Section key={partner.link_id} title={partner.name} subtitle={partner.company_name || 'Partner / accountant'} action={<Badge status={partner.status}>{partner.relation_type || partner.partner_type}</Badge>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Email" value={partner.email} />
        <Field label="Phone" value={partner.phone} />
        <Field label="Commission" value={partner.commission_value != null ? `${partner.commission_type || ''} ${partner.commission_value}` : '—'} />
        <Field label="Primary" value={partner.is_primary ? 'Yes' : 'No'} />
      </div>
    </Section>
  )) : <Empty label="Δεν υπάρχουν συνδεδεμένοι συνεργάτες." />}</div>
}

function Equipment({ assets }) {
  return <div className="space-y-4">{assets.length ? assets.map((asset) => (
    <Section key={asset.id} title={`${asset.asset_type || 'Asset'} · ${asset.brand || ''} ${asset.model || ''}`.trim()} subtitle={asset.location_name || 'Client level asset'} action={<Badge status={asset.integration_status || asset.status}>{asset.integration_status || asset.status}</Badge>}>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Field label="Serial" value={asset.serial_number} mono />
        <Field label="Fiscal serial" value={asset.fiscal_serial} mono />
        <Field label="TID" value={asset.terminal_id} mono />
        <Field label="MID" value={asset.merchant_id} mono />
        <Field label="Ownership" value={asset.ownership} />
        <Field label="Connection" value={asset.connection_type} />
        <Field label="Installed" value={asset.installed_at} />
        <Field label="Status" value={asset.status} />
      </div>
      {asset.pos_details ? <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl bg-blue-50/60 p-4 md:grid-cols-4"><Field label="Bank / acquirer" value={asset.pos_details.bank_name} /><Field label="ACQ" value={asset.pos_details.acq_value} mono /><Field label="Middleware" value={asset.pos_details.middleware_name} /><Field label="Middleware endpoint" value={[asset.pos_details.middleware_ip, asset.pos_details.middleware_port].filter(Boolean).join(':')} mono /></div> : null}
      {asset.network?.length ? <div className="mt-5 overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="text-left text-xs uppercase text-slate-500"><th className="pb-2 pr-4">Interface</th><th className="pb-2 pr-4">IP</th><th className="pb-2 pr-4">Gateway</th><th className="pb-2 pr-4">DNS</th><th className="pb-2 pr-4">Port</th><th className="pb-2">External</th></tr></thead><tbody>{asset.network.map((net) => <tr key={net.id} className="border-t border-slate-100"><td className="py-2 pr-4">{net.interface_name || '—'}</td><td className="py-2 pr-4 font-mono text-xs">{net.ip_address || '—'}</td><td className="py-2 pr-4 font-mono text-xs">{net.gateway || '—'}</td><td className="py-2 pr-4 font-mono text-xs">{[net.dns1, net.dns2].filter(Boolean).join(' / ') || '—'}</td><td className="py-2 pr-4 font-mono text-xs">{net.port || '—'}</td><td className="py-2 font-mono text-xs">{net.external_ip || '—'}</td></tr>)}</tbody></table></div> : null}
      {asset.notes ? <p className="mt-4 whitespace-pre-wrap text-sm text-slate-600">{asset.notes}</p> : null}
    </Section>
  )) : <Empty label="Δεν υπάρχει καταχωρημένος εξοπλισμός." />}</div>
}

function Employees({ employees }) {
  return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{employees.length ? employees.map((employee) => (
    <Section key={employee.id} title={employee.full_name} subtitle={employee.location_name || employee.position || 'Employee'} action={<span className={`rounded-full px-2.5 py-1 text-xs ${employee.official_qr_payload ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{employee.official_qr_payload ? 'QR ready' : 'QR missing'}</span>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="ΑΦΜ" value={employee.vat_number} mono />
        <Field label="AMKA" value={employee.amka} mono />
        <Field label="Phone" value={employee.phone} />
        <Field label="Email" value={employee.email} />
        <Field label="Status" value={employee.employment_status} />
        <Field label="Schedule" value={employee.schedule} />
      </div>
      <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">QR card workflow is enabled by the data model. Official ERGANI QR payload is used when available; Codevaro will not invent a substitute QR.</div>
    </Section>
  )) : <Empty label="Δεν υπάρχουν εργαζόμενοι." />}</div>
}

function Credentials({ credentials }) {
  return <div className="space-y-4">{credentials.length ? credentials.map((credential) => (
    <Section key={credential.id} title={credential.label || credential.credential_type} subtitle={credential.credential_type} action={<Badge status={credential.status}>{credential.status}</Badge>}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div><div className="text-xs font-medium uppercase text-slate-500">Username</div><div className="mt-1"><CredentialValue value={credential.username} /></div></div>
        <div><div className="text-xs font-medium uppercase text-slate-500">Password</div><div className="mt-1"><CredentialValue value={credential.password} /></div></div>
        <div><div className="text-xs font-medium uppercase text-slate-500">Email</div><div className="mt-1"><CredentialValue value={credential.email} /></div></div>
        <div><div className="text-xs font-medium uppercase text-slate-500">Secret</div><div className="mt-1"><CredentialValue value={credential.secret} /></div></div>
        <div><div className="text-xs font-medium uppercase text-slate-500">Consumer</div><div className="mt-1"><CredentialValue value={credential.consumer_key} /></div></div>
        <Field label="Login URL" value={credential.login_url} />
      </div>
      {credential.notes ? <p className="mt-4 text-sm text-slate-600">{credential.notes}</p> : null}
    </Section>
  )) : <Empty label="Δεν υπάρχουν credentials." />}</div>
}

function Jobs({ jobs }) {
  return <div className="space-y-4">{jobs.length ? jobs.map((job) => (
    <Section key={job.id} title={job.title || job.job_type || 'Job'} subtitle={`${job.location_name || 'Client'} · ${dateTime(job.appointment_start || job.scheduled_date)}`} action={<Badge status={job.status}>{job.status}</Badge>}>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Field label="Type" value={job.job_type} />
        <Field label="Priority" value={job.priority} />
        <Field label="Agreed" value={money(job.agreed_price)} />
        <Field label="Charged" value={money(job.actual_charge)} />
        <Field label="Payment" value={job.paid_status} />
      </div>
      {job.requested_work ? <div className="mt-4"><div className="text-xs font-medium uppercase text-slate-500">Requested work</div><p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{job.requested_work}</p></div> : null}
      {job.performed_work ? <div className="mt-4"><div className="text-xs font-medium uppercase text-slate-500">Performed work</div><p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{job.performed_work}</p></div> : null}
      {job.line_items?.length ? <div className="mt-4 rounded-xl bg-slate-50 p-4"><div className="mb-2 text-xs font-semibold uppercase text-slate-500">Services / products</div>{job.line_items.map((item) => <div key={item.id} className="flex justify-between gap-4 border-t border-slate-200 py-2 text-sm first:border-t-0"><span>{item.description || item.item_type}</span><span className="font-medium">{item.quantity || 1} × {money(item.unit_price)} = {money(item.line_total)}</span></div>)}</div> : null}
      <div className="mt-4 flex flex-wrap gap-2 text-xs"><span className={`rounded-full px-2.5 py-1 ${job.google_sync_enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>Google Calendar {job.google_sync_enabled ? 'enabled' : 'not linked'}</span>{job.calendar_links?.map((link) => <span key={link.id} className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">{link.provider}: {link.sync_status}</span>)}</div>
    </Section>
  )) : <Empty label="Δεν υπάρχουν εργασίες." />}</div>
}

function Empty({ label }) {
  return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">{label}</div>
}